import React, { useMemo } from "react";
import { FaPlay, FaPause, FaBackward, FaForward, FaCar } from "react-icons/fa";
import { IoTimerOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { TbRewindForward15, TbRewindBackward15 } from "react-icons/tb";
import { CiCircleList } from "react-icons/ci";
import SleepTimer from "./SleepTimer";
import WaveformSlider from "./WaveformSlider";
import DetailHeader from "./DetailHeader";
import { useNavigate } from "react-router-dom";

const IconButton = ({ children, onClick, color }) => (
    <button
        className="p-2 rounded-full transition-all"
        onClick={onClick}
        style={{ color }}
    >
        {children}
    </button>
);

// Changed to accept a single 'props' object
export default function Listen({
    book,
    currentIndex,
    setCurrentIndex,
    setIsPlaying,
    audioRef,
    formatTime,
    currentTime,
    duration,
    setCurrentTime,
    handlePrev,
    togglePlay,
    isPlaying,
    handleNext,
    setShowTimerModal,
    setShowCarModeModal,
    showTimerModal,
    setSleepTime,
    sleepTime,
    showCarModeModal,
    toggleAudioListModal,
    showAudioListModal,
    textColor,
    bookId
}) {
    const bookImageUrl = useMemo(() => book.imageUrl, [book]);
    const navigate = useNavigate();

    // Get the currently playing audio's name and link
    const currentAudioName = book.audioFileNames[currentIndex];
    const currentAudioLink = book.audioUrls[currentIndex]; // Assuming book.audioFiles holds the links

    const handleBack = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        navigate("/");
    };

    return (
        <div className="flex flex-col items-center text-center space-y-6 relative">
            <DetailHeader
                title={book.title}
                onBack={handleBack}
                textColor={textColor === "#fff" ? "text-white" : "text-white"} // You might want to review this textColor logic
                bookDescription={book.description}
                bookAuthor={book.author}
                // Pass the current audio link and name to DetailHeader
                currentAudioLink={currentAudioLink}
                currentAudioName={currentAudioName}
                bookId={bookId}
            />
            {/* Book Cover */}
            <img
                src={bookImageUrl}
                alt={book.title}
                className="w-auto h-[350px] rounded-3xl shadow-xl object-cover mx-auto"
            />

            {/* Chapter List */}
            <div className="flex flex-col justify-center items-center pointer-events-none">
                <div className="h-24 flex flex-col justify-center overflow-hidden pointer-events-auto">
                    <AnimatePresence mode="popLayout">
                        {book.audioFileNames
                            .map((chapter, index) => ({
                                name: chapter,
                                position: index - currentIndex,
                            }))
                            .filter((item) => Math.abs(item.position) <= 1)
                            .map((item) => (
                                <motion.p
                                    key={item.name}
                                    initial={{ opacity: 0, y: item.position * 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -item.position * 30 }}
                                    transition={{ duration: 0.3 }}
                                    className={`text-lg cursor-pointer transition-all duration-300 ${item.position === 0
                                        ? "font-semibold text-indigo-500 text-xl"
                                        : "text-indigo-400 opacity-70"
                                        }`}
                                    onClick={() => {
                                        setCurrentIndex(currentIndex + item.position);
                                        setIsPlaying(true);
                                        audioRef.current.play();
                                    }}
                                >
                                    {item.name}
                                </motion.p>
                            ))}
                    </AnimatePresence>
                </div>
            </div>

            {/* Audio List Toggle */}
            <button
                className="text-[var(--app-color)] p-2 rounded-full"
                onClick={toggleAudioListModal}
            >
                <CiCircleList size={24} />
            </button>

            {/* Waveform Slider */}
            <div className="w-full max-w-md flex items-center justify-between px-4 text-gray-500">
                <span>{formatTime(currentTime)}</span>
                <WaveformSlider
                    currentTime={currentTime}
                    duration={duration}
                    setCurrentTime={setCurrentTime}
                    audioRef={audioRef}
                    isPlaying={isPlaying}
                />
                <span>{formatTime(duration)}</span>
            </div>

            {/* Player Controls */}
            <div className="flex items-center justify-center space-x-6 mt-4">
                <IconButton onClick={() => (audioRef.current.currentTime -= 15)} color={textColor}>
                    <TbRewindBackward15 size={24} />
                </IconButton>
                <IconButton onClick={handlePrev} color={textColor}>
                    <FaBackward size={24} />
                </IconButton>
                <button
                    className="bg-[var(--app-color)] text-white p-4 rounded-full shadow-xl hover:scale-105 transition-transform"
                    onClick={togglePlay}
                    style={{ color: textColor }} // Apply textColor here if it's meant for the icon, otherwise it's text-white
                >
                    {isPlaying ? <FaPause size={24} /> : <FaPlay size={24} />}
                </button>
                <IconButton onClick={handleNext} color={textColor}>
                    <FaForward size={24} />
                </IconButton>
                <IconButton onClick={() => (audioRef.current.currentTime += 15)} color={textColor}>
                    <TbRewindForward15 size={24} />
                </IconButton>
            </div>

            {/* Extra Controls */}
            <div className="flex justify-between w-full max-w-xs text-xs text-gray-600 mt-6 px-6">
                <div
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() => setShowTimerModal(true)}
                >
                    <IoTimerOutline size={24} style={{ color: textColor }} />
                </div>
                <div
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() => setShowCarModeModal(true)}
                >
                    <FaCar size={24} style={{ color: textColor }} />
                </div>
            </div>
            <AnimatePresence>
                {showTimerModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50 w-full h-full"
                    >
                        <div className="bg-white p-5 rounded-lg shadow-lg w-full h-full flex flex-col items-center justify-center space-y-6 relative">
                            <button
                                onClick={() => setShowTimerModal(false)}
                                className="absolute top-4 right-4 text-blue-500 text-lg"
                            >
                                Жабу
                            </button>
                            <button
                                onClick={() => {
                                    setSleepTime(0);
                                    setShowTimerModal(false);
                                }}
                                className="absolute top-4 left-4 text-blue-500 text-lg"
                                style={{ margin: 0 }}
                            >
                                Болдырмау
                            </button>
                            <SleepTimer sleepTime={sleepTime} setSleepTime={setSleepTime} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {showCarModeModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50 w-full h-full"
                    >
                        <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col w-full h-full relative">
                            <button
                                onClick={() => setShowCarModeModal(false)}
                                className="absolute top-4 right-4 text-blue-500 text-lg"
                            >
                                Жабу
                            </button>
                            <div className="flex-1 flex flex-col items-center justify-center border-b pb-4">
                                <img
                                    src={book.imageUrl}
                                    alt={book.title}
                                    className="w-auto h-[350px] rounded-[30px] mx-auto"
                                />
                                <h2 className="text-2xl font-bold text-center mt-2">{book.audioFileNames[currentIndex]}</h2>
                            </div>
                            <div className="flex-1 flex justify-center items-center relative">
                                <div className="absolute left-10">
                                    <button onClick={() => audioRef.current.currentTime -= 15}>
                                        <TbRewindBackward15 size={32} />
                                    </button>
                                </div>
                                <div className="absolute right-10">
                                    <button onClick={() => audioRef.current.currentTime += 15}>
                                        <TbRewindForward15 size={32} />
                                    </button>
                                </div>
                                <button
                                    onClick={togglePlay}
                                    className="p-4 bg-gray-200 rounded-full flex items-center justify-center"
                                    style={{ width: 54, height: 54 }}
                                >
                                    {isPlaying ? <FaPause size={54} /> : <FaPlay size={54} />}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {showAudioListModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50 w-full h-full"
                    >
                        <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col w-full h-full relative">
                            <button
                                onClick={() => toggleAudioListModal()}
                                className="absolute top-4 right-4 text-blue-500 text-lg"
                            >
                                Жабу
                            </button>
                            <div className="mt-6 overflow-y-auto">
                                <AnimatePresence mode="popLayout">
                                    {book.audioFileNames
                                        .map((chapter, index) => ({
                                            name: chapter,
                                            position: index - currentIndex,
                                        }))
                                        .map((item) => (
                                            <motion.p
                                                key={item.name}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.3 }}
                                                className={`text-lg cursor-pointer transition-all duration-300 
                                                ${item.position === 0 ? "font-bold text-indigo-500 text-xl" : "text-indigo-300 opacity-70"}`}
                                                onClick={() => {
                                                    setCurrentIndex(currentIndex + item.position);
                                                    setIsPlaying(true);
                                                    audioRef.current.play();
                                                }}
                                            >
                                                {item.name}
                                            </motion.p>
                                        ))}
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}